/**
 * Where Stripe sends the browser back to.
 *
 * **The success page must not claim the subscription is active.** Checkout
 * completing means Stripe took the payment; the entitlement in AEGIS changes
 * when the *webhook* arrives and `applyStripeEvent` writes the row. Those are
 * usually milliseconds apart and occasionally are not. A page that says
 * "You're on Pro" before the webhook lands is a page that lies to a user whose
 * chart still shows the free tier, and the support ticket that follows is worse
 * than a moment of honesty here.
 *
 * So it polls the subscription endpoint, says plainly that it is waiting, and
 * tells the user their payment is safe either way.
 */
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { api } from '../lib/api.js';
import { Link } from '../lib/router.js';
import { useSession } from '../lib/session.js';

const POLL_MS = 2000;
const GIVE_UP_AFTER_MS = 30_000;

export function CheckoutSuccessScreen(): JSX.Element {
  const { refresh } = useSession();
  const [plan, setPlan] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      try {
        const sub = await api.subscription();
        if (cancelled) return;
        if (sub.plan !== 'free') {
          setPlan(sub.features.name);
          // The session's cached user carries the plan; refresh it so gating
          // elsewhere in the app agrees with this page.
          void refresh();
          return;
        }
      } catch {
        // Keep polling — a transient failure here is not an answer.
      }
      if (Date.now() - startedAt > GIVE_UP_AFTER_MS) {
        if (!cancelled) setTimedOut(true);
        return;
      }
      setTimeout(() => void tick(), POLL_MS);
    };

    void tick();
    return () => { cancelled = true; };
  }, [refresh]);

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">AEGIS</div>

        {plan ? (
          <>
            <h1 className="auth-title">You're on {plan}</h1>
            <p className="auth-sub">
              Written analysis and your journal overlay are on every chart now.
            </p>
          </>
        ) : timedOut ? (
          <>
            <h1 className="auth-title">Payment received</h1>
            <p className="auth-sub">
              Your subscription has not appeared here yet. This is a delay on our side,
              not a problem with your payment — nothing needs doing and nothing will be
              charged twice. It usually resolves within a few minutes. If it does not,
              the receipt from Stripe is the record.
            </p>
          </>
        ) : (
          <>
            <h1 className="auth-title">Payment received</h1>
            <p className="auth-sub">Activating your plan…</p>
          </>
        )}

        <div className="settings-links">
          <Link to="/" className="btn btn-primary">Open the chart</Link>
          <Link to="/settings" className="btn btn-quiet">Settings</Link>
        </div>
      </div>
    </div>
  );
}

export function CheckoutCancelledScreen(): JSX.Element {
  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">AEGIS</div>
        <h1 className="auth-title">Checkout cancelled</h1>
        <p className="auth-sub">
          Nothing was charged. Your account is unchanged and everything on the free
          plan still works — Layer 1 annotations are on every chart regardless.
        </p>
        <div className="settings-links">
          <Link to="/pricing" className="btn btn-primary">Back to plans</Link>
          <Link to="/" className="btn btn-quiet">Open the chart</Link>
        </div>
      </div>
    </div>
  );
}
